import { createSlice } from '@reduxjs/toolkit'
import { Position } from 'geojson'
import { PROGRESS_TYPES, TASK_STATUSES } from 'utils/constants'
import { appendTrajectoryPoint } from 'utils/robotTrajectory'
import type { IRobotInfo, IRobotState, IRobotTelemetry, ServiceCalibAwaitOutcome } from 'utils/interfaces'
import { IDLE_CATALOG_OPERATION } from 'utils/selectCatalogOperation'
import { noteTelemetryWipesFromSetTelemetry, telemWipeDebugEnabled } from 'utils/telemWipeDebug'

export const initialState: IRobotState = {
  loadingProgress: PROGRESS_TYPES.IDLE,
  robotInfo: undefined,
  telemetry: {} as IRobotTelemetry,
  trajectory: {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [],
    },
    properties: {},
  },
  jobInfo: {
    creatingProgress: PROGRESS_TYPES.IDLE,
    uploadingProgress: PROGRESS_TYPES.IDLE,
  },
  lidarMapLayer: {
    show: true,
    layer: undefined,
    loadingProgress: PROGRESS_TYPES.IDLE,
    loadingMapName: undefined,
  },
  serviceModeSwitchOptimistic: null,
}

export const robotSlice = createSlice({
  name: 'robot',
  initialState: initialState,
  reducers: {
    setLoadingProgress: (state: IRobotState, action: { payload: PROGRESS_TYPES }) => {
      state.loadingProgress = action.payload
    },
    setRobotInfo: (state: IRobotState, action: { payload: IRobotInfo | undefined | null }) => {
      // If payload is undefined or null, clear the robot info
      if (!action.payload) {
        state.robotInfo = undefined
        return
      }

      // If this is the first time, always update
      if (!state.robotInfo) {
        state.robotInfo = action.payload
        return
      }

      const newOdysseysIds =
        action.payload.robotRaw?.odysseys
          ?.map(o => o.id)
          .sort()
          .join(',') || ''
      const prevOdysseysIds =
        state.robotInfo?.robotRaw?.odysseys
          ?.map(o => o.id)
          .sort()
          .join(',') || ''

      // Check if task IDs changed
      const taskIdsChanged = newOdysseysIds !== prevOdysseysIds

      // Check if task statuses changed (even if IDs are the same)
      let taskStatusesChanged = false
      if (!taskIdsChanged) {
        // Same task IDs - check if any status changed
        const newOdysseysStatuses =
          action.payload.robotRaw?.odysseys
            ?.map(o => `${o.id}:${o.status || ''}`)
            .sort()
            .join(',') || ''
        const prevOdysseysStatuses =
          state.robotInfo?.robotRaw?.odysseys
            ?.map(o => `${o.id}:${o.status || ''}`)
            .sort()
            .join(',') || ''
        taskStatusesChanged = newOdysseysStatuses !== prevOdysseysStatuses
      }

      const tasksChanged = taskIdsChanged || taskStatusesChanged

      // Check if important robot state fields have changed
      const activeModeChanged = action.payload.activeMode !== state.robotInfo.activeMode
      const modeSwitchPendingChanged =
        JSON.stringify(action.payload.modeSwitchPending ?? null) !==
        JSON.stringify(state.robotInfo.modeSwitchPending ?? null)
      const activeCommandChanged = action.payload.activeCommand !== state.robotInfo.activeCommand
      const networkStatusChanged = action.payload.networkStatus !== state.robotInfo.networkStatus
      const statusChanged = action.payload.status !== state.robotInfo.status
      const jobChanged =
        action.payload.activeTask?.id !== state.robotInfo.activeTask?.id ||
        action.payload.activeTask?.status !== state.robotInfo.activeTask?.status

      // Check if mission sequence number changed (for Odyssey missions)
      const missionSequenceChanged =
        action.payload.robotRaw?.last_mission_sequence_number !== state.robotInfo.robotRaw?.last_mission_sequence_number

      const facilityIdChanged = action.payload.facilityId !== state.robotInfo.facilityId
      const facilityNameChanged = action.payload.facilityName !== state.robotInfo.facilityName

      // Check if mission status (progress) has changed by comparing mission_status_last
      // This is important for real-time progress updates
      const currentTaskId = action.payload.robotRaw?.last_job_id
      const prevJobId = state.robotInfo.robotRaw?.last_job_id

      let missionStatusChanged = false
      if (currentTaskId && prevJobId && currentTaskId === prevJobId) {
        // Same job - check if mission_status_last has changed
        const currentJob = action.payload.robotRaw?.odysseys?.find(job => job.id === currentTaskId)
        const prevJob = state.robotInfo.robotRaw?.odysseys?.find(job => job.id === prevJobId)

        // Compare mission_status_last by serializing to JSON (simple deep comparison)
        const currentStatus = JSON.stringify(currentJob?.mission_status_last)
        const prevStatus = JSON.stringify(prevJob?.mission_status_last)
        missionStatusChanged = currentStatus !== prevStatus
      }

      // Check if Gausium live_execution map position/id changed (drives floor PNG overlay & robot icon).
      // Without this check the deduplicator would discard fresh telemetry-only updates and the
      // robot icon on the GS map would freeze even though the backend keeps polling the vendor.
      const prevLive = (state.robotInfo.robotRaw as any)?.live_execution
      const newLive = (action.payload.robotRaw as any)?.live_execution
      const prevLiveMap = prevLive?.map
      const newLiveMap = newLive?.map
      const liveMapIdChanged = (prevLiveMap?.id ?? null) !== (newLiveMap?.id ?? null)
      const liveMapPreviewUrlChanged = (prevLiveMap?.preview_url ?? null) !== (newLiveMap?.preview_url ?? null)
      const prevLivePos = prevLiveMap?.position
      const newLivePos = newLiveMap?.position
      const liveMapPositionChanged =
        !!prevLivePos !== !!newLivePos ||
        (!!prevLivePos &&
          !!newLivePos &&
          (Math.abs((prevLivePos.x ?? 0) - (newLivePos.x ?? 0)) > 0.5 ||
            Math.abs((prevLivePos.y ?? 0) - (newLivePos.y ?? 0)) > 0.5 ||
            Math.abs((prevLivePos.angle ?? 0) - (newLivePos.angle ?? 0)) > 0.5))
      const liveExecutionChanged = liveMapIdChanged || liveMapPreviewUrlChanged || liveMapPositionChanged

      // Check if lastPosition changed
      const prevLastPosition = state.robotInfo.lastPosition?.coordinates
      const newLastPosition = action.payload.lastPosition?.coordinates
      const lastPositionChanged =
        // Position appeared or disappeared
        (!prevLastPosition && newLastPosition) ||
        (prevLastPosition && !newLastPosition) ||
        // Position coordinates changed significantly
        (prevLastPosition &&
          newLastPosition &&
          (Math.abs(prevLastPosition[0] - newLastPosition[0]) > 0.0001 ||
            Math.abs(prevLastPosition[1] - newLastPosition[1]) > 0.0001))

      const systemDiagnosticsChanged =
        JSON.stringify(action.payload.robotRaw?.system_diagnostics ?? null) !==
        JSON.stringify(state.robotInfo.robotRaw?.system_diagnostics ?? null)

      const activeModeMetadataChanged =
        JSON.stringify(action.payload.activeModeMetadata ?? null) !==
        JSON.stringify(state.robotInfo.activeModeMetadata ?? null)

      const catalogOperationChanged =
        JSON.stringify(action.payload.catalogOperation ?? null) !==
        JSON.stringify(state.robotInfo.catalogOperation ?? null)

      // Update if tasks changed, any important robot state field changed, mission sequence changed,
      // mission status changed, lastPosition changed, Gausium floor-map pose/preview changed,
      // or aggregated /System diagnostics changed.
      const willUpdate =
        tasksChanged ||
        activeModeChanged ||
        modeSwitchPendingChanged ||
        activeCommandChanged ||
        networkStatusChanged ||
        statusChanged ||
        jobChanged ||
        missionSequenceChanged ||
        missionStatusChanged ||
        lastPositionChanged ||
        liveExecutionChanged ||
        facilityIdChanged ||
        facilityNameChanged ||
        systemDiagnosticsChanged ||
        activeModeMetadataChanged ||
        catalogOperationChanged
      if (willUpdate) {
        state.robotInfo = action.payload
      }
    },
    updateRobotMapList: (state: IRobotState, action: { payload: string[] | null | undefined }) => {
      if (state.robotInfo) {
        state.robotInfo.map_list = action.payload
      }
    },
    setTelemetry: (
      state: IRobotState,
      action: {
        payload: Partial<IRobotTelemetry> & {
          robotModeSync?: {
            activeMode?: import('utils/constants').ROBOT_MODES
            modeSwitchPending?: import('utils/interfaces').IModeSwitchPending | null
            activeModeMetadata?: import('utils/interfaces').IRobotInfo['activeModeMetadata']
            catalogOperation?: import('utils/selectCatalogOperation').RobotCatalogOperation | null
            catalogServiceActionType?: number | null
          }
        }
      }
    ) => {
      const payload = action.payload as any
      const { robotModeSync, ...telemetryFields } = payload

      const wipeDebug = telemWipeDebugEnabled()
      const prevTelemetrySnapshot = wipeDebug && state.telemetry ? { ...state.telemetry } : null

      Object.assign(state.telemetry!, telemetryFields)

      // Explicitly clear fields that are set to undefined in payload
      // Note: undefined properties are not enumerable, so Object.keys() won't include them
      const optionalFields: Array<keyof IRobotTelemetry> = [
        'robotBattery',
        'joystickBattery',
        'dirtyWaterTankFilling',
        'temperatureControlUnit',
        'temperatureChassis',
        'temperatureLidar',
        'temperatureExternal',
        'trashFullness',
        'localization_info',
        'firmware_info',
        'availableMaintenanceModes',
        'onboardServicePoints',
        'system_diagnostics',
        'vcu',
        'charger',
        'ntrip',
      ]

      optionalFields.forEach(field => {
        if (Object.prototype.hasOwnProperty.call(telemetryFields, field) && telemetryFields[field] === undefined) {
          ;(state.telemetry as any)[field] = undefined
        }
      })

      if (wipeDebug && prevTelemetrySnapshot) {
        noteTelemetryWipesFromSetTelemetry({
          prevTelemetry: prevTelemetrySnapshot as Record<string, unknown>,
          nextTelemetry: state.telemetry as unknown as Record<string, unknown>,
          telemetryFields: telemetryFields as Record<string, unknown>,
          serial: state.robotInfo?.serialNumber ?? null,
          vendor: state.robotInfo?.vendor ?? null,
        })
      }

      if (robotModeSync && state.robotInfo) {
        if (robotModeSync.activeMode !== undefined) {
          state.robotInfo.activeMode = robotModeSync.activeMode
        }
        if (Object.prototype.hasOwnProperty.call(robotModeSync, 'modeSwitchPending')) {
          state.robotInfo.modeSwitchPending = robotModeSync.modeSwitchPending
        }
        if (Object.prototype.hasOwnProperty.call(robotModeSync, 'activeModeMetadata')) {
          state.robotInfo.activeModeMetadata = robotModeSync.activeModeMetadata
        }
        if (Object.prototype.hasOwnProperty.call(robotModeSync, 'catalogOperation')) {
          state.robotInfo.catalogOperation = robotModeSync.catalogOperation ?? IDLE_CATALOG_OPERATION
        }
        if (Object.prototype.hasOwnProperty.call(robotModeSync, 'catalogServiceActionType')) {
          state.robotInfo.catalogServiceActionType = robotModeSync.catalogServiceActionType ?? null
        }
      }
    },
    pushPositionToTrajectory: (state: IRobotState, action: { payload: Position }) => {
      state.trajectory.geometry.coordinates = appendTrajectoryPoint(
        state.trajectory.geometry.coordinates,
        action.payload
      )
    },
    setCreatingJobProgress: (state: IRobotState, action: { payload: PROGRESS_TYPES }) => {
      state.jobInfo.creatingProgress = action.payload
    },
    setUploadingJobProgress: (state: IRobotState, action: { payload: PROGRESS_TYPES }) => {
      state.jobInfo.uploadingProgress = action.payload
    },
    setJobId: (state: IRobotState, action: { payload: number }) => {
      if (state.robotInfo?.activeTask) {
        state.robotInfo.activeTask.id = action.payload
      }
    },
    setJobStatus: (state: IRobotState, action: { payload: TASK_STATUSES }) => {
      if (state.robotInfo?.activeTask) {
        state.robotInfo.activeTask.status = action.payload
      }
    },
    resetRobot: () => {
      return initialState
    },
    setShowLidarMap: (state: IRobotState, action: { payload: boolean }) => {
      state.lidarMapLayer.show = action.payload
    },
    setLidarMapLayer: (state: IRobotState, action: { payload: any }) => {
      state.lidarMapLayer.layer = action.payload
    },
    setLidarMapLoadingProgress: (state: IRobotState, action: { payload: PROGRESS_TYPES }) => {
      state.lidarMapLayer.loadingProgress = action.payload
    },
    setLidarMapLoadingMapName: (state: IRobotState, action: { payload: string | undefined }) => {
      state.lidarMapLayer.loadingMapName = action.payload
    },
    setLidarMapLoadingState: (
      state: IRobotState,
      action: { payload: { progress: PROGRESS_TYPES; name?: string; robotId?: number; facilityId?: number | null } }
    ) => {
      state.lidarMapLayer.loadingProgress = action.payload.progress
      state.lidarMapLayer.loadingMapName = action.payload.name
      state.lidarMapLayer.loadingRobotId = action.payload.robotId
      state.lidarMapLayer.loadingFacilityId = action.payload.facilityId
    },
    setServiceModeSwitchOptimistic: (
      state: IRobotState,
      action: {
        payload: {
          mode: import('utils/constants').ROBOT_MODES
          actionKey: string
          awaitCalib?: ServiceCalibAwaitOutcome
        }
      }
    ) => {
      state.serviceModeSwitchOptimistic = {
        mode: action.payload.mode,
        actionKey: action.payload.actionKey,
        startedAt: Date.now(),
        awaitCalib: action.payload.awaitCalib,
      }
    },
    clearServiceModeSwitchOptimistic: (state: IRobotState) => {
      state.serviceModeSwitchOptimistic = null
    },
  },
})

export const RobotActions = robotSlice.actions

export default robotSlice.reducer
